/**
 * ProfileEditSheet (profile-tab-reskin) — the "My info" edit bottom sheet.
 *
 * The shared ClientSheet glass idiom, made editable: all four reserved fields at
 * once, dirty tracking (Save disabled until something changes AND validates),
 * explicit Save/Cancel, drag-to-dismiss, and open/close animation (owned by
 * ClientSheet). `open`-controlled by the parent: Save issues the PATCH, and on
 * SUCCESS the parent flips `open` false (animated close); a failure rejects, is
 * caught here (no unhandled rejection), keeps the sheet open, and surfaces inline.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ProfileMyInfo } from '@/types/profile';
import { ClientSheet, clientSheetStyles as sheet } from '@/components/client/ClientSheet';
import {
  isMyInfoDirty,
  isMyInfoValid,
  myInfoEmailError,
  myInfoFrom,
  myInfoInput,
  myInfoToSave,
  MY_INFO_MAX_LENGTH as FIELD_MAX_LENGTH,
  type MyInfoField as FieldKey,
  type MyInfoValues as FieldState,
} from './myInfoRules';
import styles from './ProfilePage.module.css';

export function ProfileEditSheet({
  open,
  initialField,
  initial,
  saving,
  error,
  onSave,
  onClose,
}: {
  open: boolean;
  initialField: FieldKey;
  initial: ProfileMyInfo;
  saving: boolean;
  error: boolean;
  /** Issues the PATCH; resolves on success (parent closes) and rejects on failure. */
  onSave: (values: FieldState) => Promise<void>;
  onClose: () => void;
}) {
  const [values, setValues] = useState<FieldState>(() => myInfoFrom(initial));
  const [activeField, setActiveField] = useState<FieldKey>(initialField);

  // Reset the form each time the sheet (re)opens so it reflects current values.
  useEffect(() => {
    if (!open) return;
    setValues(myInfoFrom(initial));
    setActiveField(initialField);
  }, [open, initial, initialField]);

  const dirty = useMemo(() => isMyInfoDirty(values, initial), [values, initial]);

  // Client-side mirror of the server's reserved-field rules — Save stays disabled
  // (the PATCH would 400) until every field is non-empty, within the API caps,
  // and the email looks valid.
  const valid = useMemo(() => isMyInfoValid(values), [values]);

  const set = (key: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: myInfoInput(key, e.target.value) }));

  const handleSave = async () => {
    // Catch the mutation rejection so it never escapes as an unhandled rejection;
    // the inline error (driven by `error`) and the still-open sheet cover failure.
    try {
      await onSave(myInfoToSave(values));
    } catch {
      /* stay open — the inline error UI rides `error` */
    }
  };

  return (
    <ClientSheet
      open={open}
      onClose={onClose}
      kicker="Edit"
      title="Your info"
      draggable
      dismissable={!saving}
      ariaLabel="Edit your info"
      testId="profile-edit-sheet"
    >
      <div className={styles.editForm}>
        <EditField
          fieldKey="firstName"
          label="First name"
          value={values.firstName}
          onChange={set('firstName')}
          maxLength={FIELD_MAX_LENGTH.firstName}
          active={activeField === 'firstName'}
          onFocus={() => setActiveField('firstName')}
        />
        <EditField
          fieldKey="lastName"
          label="Last name"
          value={values.lastName}
          onChange={set('lastName')}
          maxLength={FIELD_MAX_LENGTH.lastName}
          active={activeField === 'lastName'}
          onFocus={() => setActiveField('lastName')}
        />
        <EditField
          fieldKey="email"
          label="Email"
          value={values.email}
          onChange={set('email')}
          type="email"
          inputMode="email"
          maxLength={FIELD_MAX_LENGTH.email}
          active={activeField === 'email'}
          onFocus={() => setActiveField('email')}
          error={myInfoEmailError(values.email) ?? undefined}
        />
        <EditField
          fieldKey="phone"
          label="Phone"
          value={values.phone}
          onChange={set('phone')}
          type="tel"
          inputMode="tel"
          maxLength={FIELD_MAX_LENGTH.phone}
          active={activeField === 'phone'}
          onFocus={() => setActiveField('phone')}
        />
      </div>

      {error && (
        <div className={sheet.error} data-testid="edit-error" role="alert">
          Couldn’t save your changes. Please try again.
        </div>
      )}

      <div className={sheet.actionsRow}>
        <button
          type="button"
          className={sheet.save}
          disabled={saving || !dirty || !valid}
          onClick={handleSave}
          data-testid="edit-save"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className={sheet.cancel} onClick={onClose} disabled={saving}>
          Cancel
        </button>
      </div>
    </ClientSheet>
  );
}

function EditField({
  fieldKey,
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  maxLength,
  active,
  onFocus,
  error,
}: {
  fieldKey: FieldKey;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  inputMode?: 'email' | 'tel' | 'text';
  maxLength: number;
  active: boolean;
  onFocus: () => void;
  error?: string;
}) {
  return (
    <label
      className={`${styles.editInput} ${active ? styles.editInputActive : ''}`}
      data-active={active ? 'true' : undefined}
    >
      <span className={styles.editLabel}>{label}</span>
      <input
        data-field={fieldKey}
        className={styles.editBox}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        maxLength={maxLength}
        autoComplete="off"
        autoCapitalize={type === 'email' ? 'none' : undefined}
        aria-invalid={error ? 'true' : undefined}
      />
      {error && <span className={styles.editFieldError}>{error}</span>}
    </label>
  );
}
